<template>
  <a
    :href="href"
    class="pill"
    :class="pillClass"
    @click.prevent="$emit('click')"
  >
    <slot>{{ label }}</slot>
  </a>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  label?: string;
  href?: string;
  variant?: 'default' | 'primary' | 'outline' | 'gray';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  href: '#'
});

defineEmits<{
  (e: 'click'): void;
}>();

const pillClass = computed(() => `pill-${props.variant}`);
</script>

<style scoped>
/* Base pill styles */
.pill {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85em;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;
}

/* Gray variant (original SearchResults style) */
.pill-gray {
  background-color: #f0f0f0;
  color: #333;
  border: 2px solid transparent;
}

.pill-gray:hover {
  background-color: #e0e0e0;
  transform: translateY(-2px);
}

/* Default/Primary variant (pink filled) */
.pill-default,
.pill-primary {
  background-color: #ff5e8a;
  color: white;
  border: 2px solid #ff5e8a;
}

.pill-default:hover,
.pill-primary:hover {
  background-color: #ff3d6f;
  border-color: #ff3d6f;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(255, 94, 138, 0.3);
}

/* Outline variant (pink outline) */
.pill-outline {
  background-color: white;
  color: #ff5e8a;
  border: 2px solid #ff5e8a;
}

.pill-outline:hover {
  background-color: #ff5e8a;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(255, 94, 138, 0.3);
}

/* Larger screens */
@media (min-width: 768px) {
  .pill {
    padding: 10px 20px;
    font-size: 0.95em;
  }
}
</style>
